import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import maplibregl, { Map as MLMap, Marker } from "maplibre-gl";

import type { BBox, ConnectedFilter, ExtraObserver, Frequency, Job, JobMode, NationalRegion, Station } from "./lib/api";
import { api, NATIONAL_REGION_BOUNDS } from "./lib/api";
import type { BasemapId } from "./lib/basemaps";
import { BASEMAPS } from "./lib/basemaps";
import { MAX_ALT, snapAltitude } from "./lib/altitudes";
import { BboxDrawer } from "./components/BboxDrawer";
import { PlaceMarkerController } from "./components/PlaceMarkerController";
import { SidePanel } from "./components/SidePanel";
import { ModePicker } from "./components/ModePicker";
import { JobProgress } from "./components/JobProgress";
import { IngestBanner } from "./components/IngestBanner";
import { AirspaceController, type AirspaceFetchStatus } from "./components/AirspaceLayerController";
import { SavedJobsLayerController } from "./components/SavedJobsLayerController";
import { StationDiagPopup } from "./components/StationDiagPopup";
import type { AirspaceClassId } from "./lib/airspace";
import { AIRSPACE_CLASSES } from "./lib/airspace";
import { ALTITUDE_STEPS_FT } from "./lib/altitudes";
import type { ObserverMeta } from "./lib/api";
import { compositeMin, extractManifestJsonFromBundle, loadObserverRaster, type ObserverRaster } from "./lib/composite";
import { MinAltitudeRasterLayer } from "./layers/MinAltitudeRasterLayer";

type BboxState = "idle" | "drawing" | "drawn" | "computing" | "done";

/** Color a marker based on whether its station is currently selected in the
 * contributions list (rep or a cluster member). Highlighted markers also grow
 * and get a heavy dark ring so the difference is unmistakable at typical
 * zoom levels. Hypothetical (what-if) markers get a gold dashed border so
 * they're unmistakable from real on-air stations. No animation — purely
 * static state. */
function applyStationMarkerStyle(el: HTMLDivElement, highlighted: boolean): void {
  const connected = el.dataset.connected === "1";
  const hypothetical = el.dataset.hypothetical === "1";
  el.style.borderRadius = "50%";
  el.style.cursor = "pointer";
  if (highlighted) {
    el.style.width = "16px";
    el.style.height = "16px";
    el.style.background = hypothetical ? "#fa3" : "#fd0";
    el.style.border = hypothetical ? "3px dashed #000" : "3px solid #000";
    el.style.boxShadow = "0 0 0 2px #fff, 0 0 4px 2px rgba(0,0,0,0.4)";
    el.style.zIndex = "10";
  } else if (hypothetical) {
    el.style.width = "12px";
    el.style.height = "12px";
    el.style.background = "#fa3";              // amber
    el.style.border = "2px dashed #fc0";       // gold dashed
    el.style.boxShadow = "0 0 0 1px rgba(0,0,0,0.5)";
    el.style.zIndex = "9";                     // above real stations
  } else {
    el.style.width = "10px";
    el.style.height = "10px";
    el.style.background = connected ? "#7c0" : "#666";
    el.style.border = "1.5px solid #fff";
    el.style.boxShadow = "0 0 0 1px rgba(0,0,0,0.5)";
    el.style.zIndex = "";
  }
}

export default function App() {
  const mapRef = useRef<MLMap | null>(null);
  const mapContainer = useRef<HTMLDivElement | null>(null);
  const markersRef = useRef<Marker[]>([]);
  const drawerRef = useRef<BboxDrawer | null>(null);
  const vsLayerRef = useRef<MinAltitudeRasterLayer | null>(null);
  const airspaceRef = useRef<AirspaceController | null>(null);
  const savedJobsRef = useRef<SavedJobsLayerController | null>(null);
  /** Latest onLoadSavedJob callback. The SavedJobsLayerController is created
   * once in map "load" and would otherwise close over a stale callback that
   * never sees frequency / altitude / etc. state changes. */
  const onLoadSavedJobRef = useRef<((j: Job) => Promise<void>) | null>(null);

  const [basemap, setBasemap] = useState<BasemapId>("opentopo");
  const [altitudeFt, setAltitudeFt] = useState(MAX_ALT);
  const [frequency, setFrequency] = useState<Frequency>("1090");
  const [opacity, setOpacity] = useState(0.6);
  const [stationFilter, setStationFilter] = useState<ConnectedFilter>("connected_only");

  const [stations, setStations] = useState<Station[]>([]);
  const [selectedStation, setSelectedStation] = useState<Station | null>(null);

  const [bboxState, setBboxState] = useState<BboxState>("idle");
  const [pendingBbox, setPendingBbox] = useState<BBox | null>(null);
  const [showModePicker, setShowModePicker] = useState(false);
  const [currentJob, setCurrentJob] = useState<Job | null>(null);
  const [jobMessage, setJobMessage] = useState<string | null>(null);
  const [ingestBusy, setIngestBusy] = useState(false);
  const [airspaceEnabled, setAirspaceEnabled] = useState<Set<AirspaceClassId>>(
    () => new Set(AIRSPACE_CLASSES.filter((c) => c.defaultOn).map((c) => c.id)),
  );
  const [airspaceOpacity, setAirspaceOpacity] = useState<Record<AirspaceClassId, number>>(
    () => Object.fromEntries(AIRSPACE_CLASSES.map((c) => [c.id, 0.4])) as Record<AirspaceClassId, number>,
  );
  const [showSavedJobs, setShowSavedJobs] = useState(true);

  // National coverage envelope: composites every cached observer into one
  // CONUS-wide overlay. Reuses vsLayerRef (only one MinAltitudeRasterLayer
  // at a time — toggling national-on clears any per-bbox coverage).
  const [nationalEnabled, setNationalEnabled] = useState(false);
  const [nationalLoading, setNationalLoading] = useState(false);
  const [nationalStatus, setNationalStatus] = useState<{
    inputs: number; built: boolean; stale: boolean; size_bytes?: number;
  } | null>(null);
  const [nationalRegion, setNationalRegion] = useState<NationalRegion>("conus");

  // Instant-coverage flow state: populated when a bbox draws successfully
  // produces an instant composite (cache-warm). Mutually exclusive with
  // currentJob — if instantBbox is set, KMZ/bundle export uses the
  // instant export endpoint instead of the job export endpoint.
  const [instantBbox, setInstantBbox] = useState<BBox | null>(null);

  // What-if analysis: hypothetical Flightline stations dropped on the map
  // by the operator. Per-session UI state only — submitting a what-if job
  // persists the list as job.extra_observers_json. Saved-job load re-
  // hydrates this from the loaded job's extra_observers array.
  const [hypotheticals, setHypotheticals] = useState<ExtraObserver[]>([]);
  const placerRef = useRef<PlaceMarkerController | null>(null);
  const [placeMode, setPlaceMode] = useState(false);
  const hypoMarkersRef = useRef<Marker[]>([]);
  /** Mirror placeMode into a ref so map-event callbacks (registered once at
   * map-load) can read the current value without re-binding. Saved-jobs and
   * station-marker clicks check this and bail when place-mode is active so
   * their handlers don't intercept the placement click. */
  const placeModeRef = useRef(false);
  useEffect(() => { placeModeRef.current = placeMode; }, [placeMode]);

  /** Mirror slider + opacity into refs so layer-creation sites
   * (tryInstant, onJobDone, onLoadSavedJob, etc.) ALWAYS use the
   * latest value when assigning layer.sliderAlt / layer.opacity, not
   * the closure-captured value from when their useCallback last ran.
   * Without this, moving the slider DURING a loadCog wait (especially
   * during a multi-minute what-if compute) leaves the rendered
   * coverage at the old altitude even though the slider UI reads the
   * new one — operator sees a stale overlay. */
  const altitudeFtRef = useRef(altitudeFt);
  const opacityRef = useRef(opacity);
  useEffect(() => { altitudeFtRef.current = altitudeFt; }, [altitudeFt]);
  useEffect(() => { opacityRef.current = opacity; }, [opacity]);

  // Airspace fetch status — surfaced in the bottom-left dock so the user
  // sees "Loading airspace…" / "Airspace fetch failed" instead of guessing
  // why outlines didn't appear.
  const [airspaceStatus, setAirspaceStatus] = useState<AirspaceFetchStatus>("idle");
  const [airspaceError, setAirspaceError] = useState<string | null>(null);

  // Per-station coverage contributions
  const [observers, setObservers] = useState<ObserverMeta[]>([]);
  const [enabledObservers, setEnabledObservers] = useState<Set<number>>(new Set());
  const observerRastersRef = useRef<Map<number, ObserverRaster>>(new Map());
  /** Original union COG grid (= bbox), captured when the bundle/job COG first
   * loads. Re-used as the composite's target grid so toggling stations doesn't
   * resize the raster to whatever per-observer bounds happen to be enabled. */
  const unionGridRef = useRef<{ width: number; height: number; bounds: { left: number; bottom: number; right: number; top: number } } | null>(null);
  const NODATA = -32767;

  // Coverage stats panel (populated when a raster is loaded)
  const [coverageStats, setCoverageStats] = useState<{
    bboxFraction: number;
    byTier: Array<{ ft: number; pct: number }>;
  } | null>(null);

  /** Jobs running on the server that this UI did NOT submit (e.g. kicked off
   * via SSH/curl during testing, or from another tab). Polled so the user
   * always sees what's happening server-side, not just their own clicks. */
  const [externalJobs, setExternalJobs] = useState<Job[]>([]);

  // Station markers: map of station_id -> DOM element for in-place style updates
  const markerElsRef = useRef<Map<number, HTMLDivElement>>(new Map());

  // Initialize map on mount
  useEffect(() => {
    if (!mapContainer.current) return;
    const map = new maplibregl.Map({
      container: mapContainer.current,
      style: BASEMAPS.opentopo.style,
      center: [-95.0, 39.0],
      zoom: 3.5,
    });
    map.addControl(new maplibregl.NavigationControl({ showCompass: false }), "top-left");
    mapRef.current = map;
    (window as any).maplibregl = maplibregl;

    map.once("load", () => {
      const ctrl = new AirspaceController(map, Array.from(airspaceEnabled));
      ctrl.setStatusListener((s, err) => {
        setAirspaceStatus(s);
        setAirspaceError(err ?? null);
      });
      ctrl.attach();
      airspaceRef.current = ctrl;

      const sj = new SavedJobsLayerController(map, (job) => {
        // Suppress saved-job loads while the operator is placing a what-if
        // marker — otherwise a click on a bbox under the cursor opens the
        // saved job instead of dropping the marker.
        if (placeModeRef.current) return;
        void onLoadSavedJobRef.current?.(job);
      });
      savedJobsRef.current = sj;
      if (showSavedJobs) sj.attach();
    });

    return () => {
      airspaceRef.current?.detach();
      airspaceRef.current = null;
      savedJobsRef.current?.detach();
      savedJobsRef.current = null;
      map.remove();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Swap basemap — MapLibre's setStyle preserves sources/layers under
  // "diff: true"... but for raster-only basemaps we want a full reload and
  // then re-attach airspace + saved-jobs + viewshed from scratch.
  //
  // Dep list is intentionally just [basemap]. showSavedJobs is handled in
  // its own effect below — don't trigger a full setStyle every time the
  // user toggles that checkbox (was causing the viewshed to disappear on
  // any saved-jobs toggle as a side effect).
  //
  // Skip the initial mount: the map was constructed with this style, and
  // calling setStyle again before the first style.load fires triggers
  // MapLibre's "Style is not done loading. Rebuilding from scratch." path.
  const basemapMountedRef = useRef(false);
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    if (!basemapMountedRef.current) {
      basemapMountedRef.current = true;
      return;
    }
    const snapshot = vsLayerRef.current?.snapshot() ?? null;

    // Defensive teardown BEFORE setStyle: explicitly detach our overlays so
    // their internal state is reset and they can re-attach cleanly. Without
    // this, controllers ended up half-initialized after setStyle and the
    // next attach() became a silent no-op (overlays vanished until hard
    // refresh).
    airspaceRef.current?.detach();
    savedJobsRef.current?.detach();

    // diff:false forces a full reload — sidesteps MapLibre's style-diffing
    // path which sometimes preserved source shells without their features
    // and made re-attach a no-op.
    map.setStyle(BASEMAPS[basemap].style, { diff: false });

    const reattach = () => {
      if (airspaceRef.current) airspaceRef.current.attach();
      if (showSavedJobs && savedJobsRef.current) savedJobsRef.current.attach();
      if (snapshot) {
        const freshLayer = new MinAltitudeRasterLayer();
        vsLayerRef.current = freshLayer;
        freshLayer.opacity = snapshot.opacity;
        freshLayer.sliderAlt = snapshot.sliderAlt;
        const airspaceBeforeId = map.getLayer("__airspace_fill__")
          ? "__airspace_fill__" : undefined;
        try {
          map.addLayer(
            freshLayer as unknown as maplibregl.CustomLayerInterface,
            airspaceBeforeId,
          );
        } catch (e) {
          console.error("[basemap] addLayer threw:", e);
        }
        freshLayer.setRasterData(
          snapshot.data, snapshot.width, snapshot.height, snapshot.nodata,
          snapshot.bounds3857,
        );
        map.triggerRepaint();
      }
    };

    if (map.isStyleLoaded()) {
      // setStyle synchronously applied (rare for raster basemaps but
      // technically possible). Reattach immediately.
      reattach();
    } else {
      map.once("style.load", reattach);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [basemap]);

  // Toggle saved-jobs overlay
  useEffect(() => {
    const ctrl = savedJobsRef.current;
    if (!ctrl) return;
    if (showSavedJobs) ctrl.attach();
    else ctrl.detach();
  }, [showSavedJobs]);

  // Suppress the currently-loaded job's bbox from the saved-jobs overlay so
  // it doesn't double-up with its own coverage rendering. Show it again when
  // the user clears the bbox / starts fresh.
  useEffect(() => {
    savedJobsRef.current?.setExcluded(
      bboxState === "done" || bboxState === "computing" ? currentJob?.id ?? null : null,
    );
  }, [currentJob, bboxState]);

  // Push airspace toggle state into the controller
  useEffect(() => {
    airspaceRef.current?.setEnabled(Array.from(airspaceEnabled));
  }, [airspaceEnabled]);
  useEffect(() => {
    airspaceRef.current?.setOpacities(airspaceOpacity);
  }, [airspaceOpacity]);

  const onAirspaceToggle = useCallback((id: AirspaceClassId, on: boolean) => {
    setAirspaceEnabled((prev) => {
      const next = new Set(prev);
      if (on) next.add(id); else next.delete(id);
      return next;
    });
  }, []);

  // ---- per-station coverage contributions ----
  const onToggleContribution = useCallback((stationId: number, on: boolean) => {
    setEnabledObservers((prev) => {
      const next = new Set(prev);
      if (on) next.add(stationId); else next.delete(stationId);
      return next;
    });
  }, []);
  const onContributionsAllOn = useCallback(() => {
    setEnabledObservers(new Set(observers.map((o) => o.station_id)));
  }, [observers]);
  const onContributionsAllOff = useCallback(() => {
    setEnabledObservers(new Set());
  }, []);

  // Render a fresh composite raster whenever the enabled set changes (or freq changes).
  // Lazily fetches per-observer COGs the first time each one is enabled.
  useEffect(() => {
    const job = currentJob;
    const layer = vsLayerRef.current;
    if (!job || !layer || observers.length === 0) return;
    const enabled = Array.from(enabledObservers);
    let cancelled = false;

    (async () => {
      // Fetch any rasters we don't yet have cached
      const fetches: Promise<ObserverRaster>[] = [];
      for (const sid of enabled) {
        if (!observerRastersRef.current.has(sid)) {
          fetches.push(loadObserverRaster(api.observerCogUrl(job.id, sid, frequency), sid));
        }
      }
      if (fetches.length > 0) {
        const loaded = await Promise.all(fetches);
        if (cancelled) return;
        for (const r of loaded) observerRastersRef.current.set(r.stationId, r);
      }

      // If everything's disabled — render an empty raster (clears the overlay)
      if (enabled.length === 0) {
        layer.setRasterData(new Float32Array(0), 0, 0, NODATA, { left: 0, bottom: 0, right: 0, top: 0 });
        mapRef.current?.triggerRepaint();
        return;
      }

      // Composite the enabled rasters and push into the layer. Per-observer
      // COGs each have their own bounds (some extend outside the bbox); we
      // sample them into the union COG's grid so the composite shape matches
      // the user's drawn bbox instead of spilling out the south or sides.
      const enabledRasters = enabled
        .map((sid) => observerRastersRef.current.get(sid))
        .filter((r): r is ObserverRaster => r !== undefined);
      if (enabledRasters.length === 0) return;
      const targetGrid = unionGridRef.current ?? layer.getRasterGrid() ?? undefined;
      const result = compositeMin(enabledRasters, NODATA, targetGrid);
      layer.setRasterData(result.data, result.width, result.height, NODATA, result.bounds);
      refreshStats();
      mapRef.current?.triggerRepaint();
    })().catch((e) => {
      console.warn("composite render failed:", e);
    });

    return () => {
      cancelled = true;
    };
  }, [enabledObservers, observers, currentJob, frequency]);

  // Load stations on mount
  const refreshStations = useCallback(() => {
    api.listStations("all")
      .then(setStations)
      .catch((e) => setJobMessage(`Failed to load stations: ${e.message}`));
  }, []);

  useEffect(() => {
    refreshStations();
  }, [refreshStations]);

  // Reflect ingest state in the UI controls
  useEffect(() => {
    const poll = async () => {
      try {
        const s = await api.ingestStatus();
        setIngestBusy(s.state === "running");
      } catch {
        /* swallow — initial boot may race */
      }
    };
    poll();
    const id = window.setInterval(poll, 4000);
    return () => window.clearInterval(id);
  }, []);

  // Watch for jobs running on the server that this UI didn't submit (kicked
  // off via SSH/curl, or in another browser tab). Surface them in the same
  // bottom-left progress dock so the operator always sees what's happening.
  useEffect(() => {
    const poll = async () => {
      try {
        const [running, queued] = await Promise.all([
          api.listJobs("running", 10),
          api.listJobs("queued", 10),
        ]);
        const myId = currentJob?.id;
        const others = [...running, ...queued].filter((j) => j.id !== myId);
        // Stable ordering by created_at desc
        others.sort((a, b) => (b.created_at ?? "").localeCompare(a.created_at ?? ""));
        setExternalJobs(others);
      } catch {
        /* network blip; next tick will retry */
      }
    };
    poll();
    const id = window.setInterval(poll, 4000);
    return () => window.clearInterval(id);
  }, [currentJob]);

  /** When an external (non-UI) job finishes, refresh the saved-jobs overlay
   * so its bbox shows up immediately. The poll above will also drop it from
   * externalJobs on the next tick. */
  const onExternalJobDone = useCallback((jobId: string) => {
    void savedJobsRef.current?.refresh();
    setExternalJobs((prev) => prev.filter((j) => j.id !== jobId));
  }, []);
  const onExternalJobFailed = useCallback((jobId: string, ev: Record<string, unknown>) => {
    setJobMessage(`Server-side job ${jobId.slice(0, 8)} failed: ${ev.error ?? "unknown"}`);
    setExternalJobs((prev) => prev.filter((j) => j.id !== jobId));
  }, []);

  /** Cancel a job (queued or running) and drop it from the UI immediately so
   * the user doesn't have to wait for the next poll tick. */
  const onCancelExternalJob = useCallback(async (jobId: string) => {
    try {
      await api.cancelJob(jobId);
      setExternalJobs((prev) => prev.filter((j) => j.id !== jobId));
    } catch (e: any) {
      setJobMessage(`Cancel failed: ${e.message}`);
    }
  }, []);

  const onCancelOwnJob = useCallback(async () => {
    if (!currentJob) return;
    try {
      await api.cancelJob(currentJob.id);
      setBboxState("idle");
      setCurrentJob(null);
      setJobMessage(null);
      drawerRef.current?.clear();
    } catch (e: any) {
      setJobMessage(`Cancel failed: ${e.message}`);
    }
  }, [currentJob]);

  const onUploadCsv = useCallback(async (file: File) => {
    try {
      await api.uploadCsv(file);
      setIngestBusy(true);
      setJobMessage(null);
    } catch (e: any) {
      setJobMessage(`Upload failed: ${e.message}`);
    }
  }, []);

  const onReingestSeed = useCallback(async () => {
    try {
      await api.reingestSeed();
      setIngestBusy(true);
      setJobMessage(null);
    } catch (e: any) {
      setJobMessage(`Re-ingest failed: ${e.message}`);
    }
  }, []);

  const onSaveBundle = useCallback(async () => {
    if (currentJob) {
      // Trigger download via a hidden anchor so the browser's filename hint stays
      window.location.href = api.bundleUrl(currentJob.id);
      return;
    }
    if (instantBbox) {
      // Build a bundle from the cache for the instant overlay
      try {
        const blob = await api.instantExport(instantBbox, "bundle", undefined, stationFilter);
        const href = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = href;
        a.download = "coverage_instant.zip";
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        setTimeout(() => URL.revokeObjectURL(href), 1000);
      } catch (e: any) {
        setJobMessage(`Bundle export failed: ${e.message ?? e}`);
      }
    }
  }, [currentJob, instantBbox, stationFilter]);

  /** Load a job by id from the server (no zip indirection). Used when the
   * user clicks one of the saved-jobs bbox outlines on the map. */
  const onLoadSavedJob = useCallback(async (job: Job): Promise<void> => {
    if (ingestBusy) {
      setJobMessage("Wait for station ingest to finish before loading saved coverage.");
      return;
    }
    try {
      setJobMessage(`Loading saved coverage ${job.id.slice(0, 8)}…`);
      // Per-observer manifest (skip if old job lacks it — coverage still loads)
      let obs: ObserverMeta[] = [];
      try {
        const m = await api.jobObservers(job.id);
        obs = m.observers ?? [];
      } catch {
        // 404 just means an old job without per-observer data; the union COG
        // still renders, just no contributions panel.
      }
      setObservers(obs);
      setEnabledObservers(new Set(obs.map((o) => o.station_id)));
      // Hydrate hypothetical-station markers if this was a what-if job —
      // re-uses the same per-session state that the bbox-flow uses.
      setHypotheticals(job.extra_observers ?? []);
      setCurrentJob(job);
      setBboxState("computing");
      removeCoverageLayer();
      const layer = new MinAltitudeRasterLayer();
      await layer.loadCog(api.cogUrl(job.id, frequency));
      unionGridRef.current = layer.getRasterGrid();
      layer.sliderAlt = altitudeFtRef.current;
      layer.opacity = opacityRef.current;
      // Defensive remove by id — handles untracked-stale-layer cases that
      // removeCoverageLayer() (which only knows about vsLayerRef.current)
      // can't catch. E.g., a basemap-swap orphan or a parallel
      // onLoadSavedJob that already added before this one finished its
      // loadCog. Without this, addLayer throws "already exists" and the
      // failed layer gets assigned to vsLayerRef, breaking subsequent
      // slider updates which then point at a phantom layer not on the map.
      if (mapRef.current?.getLayer(layer.id)) {
        mapRef.current.removeLayer(layer.id);
      }
      // Insert BELOW airspace fill so FAA outlines render on top of the viewshed
      mapRef.current?.addLayer(
        layer as unknown as maplibregl.CustomLayerInterface,
        mapRef.current?.getLayer("__airspace_fill__") ? "__airspace_fill__" : undefined,
      );
      vsLayerRef.current = layer;
      setBboxState("done");
      refreshStats();

      // Zoom to bbox
      const m = job.bbox_wkt.match(/POLYGON \(\(([^)]+)\)\)/);
      if (m && mapRef.current) {
        const coords = m[1].split(",").map((s) => s.trim().split(/\s+/).map(Number));
        const lons = coords.map((c) => c[0]);
        const lats = coords.map((c) => c[1]);
        mapRef.current.fitBounds(
          [[Math.min(...lons), Math.min(...lats)], [Math.max(...lons), Math.max(...lats)]],
          { padding: 60, animate: true },
        );
      }
      setJobMessage(`Loaded ${job.id.slice(0, 8)} · ${obs.length} observer${obs.length === 1 ? "" : "s"}`);
    } catch (e: any) {
      setJobMessage(`Load failed: ${e.message}`);
      setBboxState("idle");
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ingestBusy, frequency, altitudeFt, opacity]);

  // Keep the ref pointing at the latest closure so the SavedJobsLayer
  // controller (created once on map "load") sees current state.
  useEffect(() => {
    onLoadSavedJobRef.current = onLoadSavedJob;
  }, [onLoadSavedJob]);

  const onLoadBundle = useCallback(async (file: File) => {
    if (ingestBusy) {
      setJobMessage("Wait for station ingest to finish before loading a coverage bundle.");
      return;
    }
    try {
      setJobMessage(`Importing ${file.name}…`);
      const result = await api.importBundle(file);
      // populate per-observer state from the bundle
      const obs = result.observers ?? [];
      const merged = obs.reduce((n, o) => n + (o.merged_member_ids?.length ?? 0), 0);
      setObservers(obs);
      setEnabledObservers(new Set(obs.map((o) => o.station_id)));
      // Synthesize a Job object so the existing render pipeline takes over.
      const job: Job = {
        id: result.id,
        bbox_wkt: result.bbox_wkt,
        mode: "union",
        selected_station_id: null,
        station_filter: "all",
        status: "done",
        progress: 100,
        stations_in_scope: null,
        stations_completed: 0,
        cog_path_1090: "imported",
        cog_path_978: "imported",
        error: null,
        created_at: result.exported_at ?? new Date().toISOString(),
        completed_at: result.exported_at ?? new Date().toISOString(),
      };
      setCurrentJob(job);
      setBboxState("computing");  // triggers onJobDone path in next render
      // Render immediately
      removeCoverageLayer();
      const layer = new MinAltitudeRasterLayer();
      await layer.loadCog(api.cogUrl(result.id, frequency));
      unionGridRef.current = layer.getRasterGrid();
      layer.sliderAlt = altitudeFtRef.current;
      layer.opacity = opacityRef.current;
      // Defensive remove by id (see onLoadSavedJob for rationale)
      if (mapRef.current?.getLayer(layer.id)) {
        mapRef.current.removeLayer(layer.id);
      }
      // Insert BELOW airspace fill so FAA outlines render on top of the viewshed
      mapRef.current?.addLayer(
        layer as unknown as maplibregl.CustomLayerInterface,
        mapRef.current?.getLayer("__airspace_fill__") ? "__airspace_fill__" : undefined,
      );
      vsLayerRef.current = layer;
      setBboxState("done");
      refreshStats();

      // Zoom to the bbox so the user can see the imported coverage
      const m = result.bbox_wkt.match(/POLYGON \(\(([^)]+)\)\)/);
      if (m && mapRef.current) {
        const coords = m[1].split(",").map((s) => s.trim().split(/\s+/).map(Number));
        const lons = coords.map((c) => c[0]);
        const lats = coords.map((c) => c[1]);
        mapRef.current.fitBounds(
          [[Math.min(...lons), Math.min(...lats)], [Math.max(...lons), Math.max(...lats)]],
          { padding: 60, animate: true },
        );
      }
      setJobMessage(
        `Imported ${file.name} · ${obs.length} observer${obs.length === 1 ? "" : "s"}` +
        (merged > 0 ? ` (+${merged} merged peers)` : "")
      );
    } catch (e: any) {
      setJobMessage(`Import failed: ${e.message}`);
      setBboxState("idle");
    }
  }, [ingestBusy, frequency, altitudeFt, opacity]);

  // Highlighted station IDs = union of (rep + its cluster members) for every
  // rep currently checked in the contributions list. Recomputed from state;
  // no extra hover/selection tracking needed.
  const highlightedStationIds = useMemo(() => {
    const s = new Set<number>();
    for (const obs of observers) {
      if (!enabledObservers.has(obs.station_id)) continue;
      s.add(obs.station_id);
      for (const mid of obs.merged_member_ids) s.add(mid);
    }
    return s;
  }, [observers, enabledObservers]);

  // Kept in a ref so the marker-render effect can read the current highlight
  // set at creation time without having to depend on it (re-running on every
  // checkbox toggle would destroy and re-create every marker on the map).
  const highlightedRef = useRef<Set<number>>(highlightedStationIds);
  highlightedRef.current = highlightedStationIds;

  // Render station markers — re-render when stations or filter changes
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    for (const m of markersRef.current) m.remove();
    markersRef.current = [];
    markerElsRef.current.clear();
    const visible = stations.filter((s) => {
      if (stationFilter === "connected_only") return s.connected;
      if (stationFilter === "disconnected_only") return !s.connected;
      return true;
    });
    const currentHighlights = highlightedRef.current;
    for (const s of visible) {
      const el = document.createElement("div");
      el.className = "station-marker";
      el.dataset.connected = s.connected ? "1" : "0";
      applyStationMarkerStyle(el, currentHighlights.has(s.id));
      el.title = s.identifier;
      el.onclick = (e) => {
        // Don't open the station diagnostics popup if the operator is
        // dropping a what-if marker — they may have clicked near a real
        // station's pin by accident.
        if (placeModeRef.current) return;
        e.stopPropagation();
        setSelectedStation(s);
      };
      // IMPORTANT: MapLibre GL 4.x dropped the legacy `new Marker(element)` signature
      // in favor of `new Marker({element})`. Passing an HTMLElement as the first arg
      // silently falls back to the default blue SVG teardrop and ignores the element.
      const marker = new maplibregl.Marker({ element: el }).setLngLat([s.lon, s.lat]).addTo(map);
      markersRef.current.push(marker);
      markerElsRef.current.set(s.id, el);
    }
    return () => {
      for (const m of markersRef.current) m.remove();
      markerElsRef.current.clear();
    };
  }, [stations, stationFilter]);

  // Apply colored-pin style to markers whenever the highlighted set changes.
  // If a highlighted station isn't currently rendered (e.g. disconnected and
  // the marker filter is connected-only) it's silently skipped — the color
  // will apply next time that marker is created.
  useEffect(() => {
    for (const [id, el] of markerElsRef.current) {
      applyStationMarkerStyle(el, highlightedStationIds.has(id));
    }
  }, [highlightedStationIds, stationFilter]);

  // Station counts for the panel
  const stationCount = useMemo(() => {
    const connected = stations.filter((s) => s.connected).length;
    return { total: stations.length, connected, disconnected: stations.length - connected };
  }, [stations]);

  // ---- what-if analysis ----
  // AGL/feet conversion + identifier formatter. Identifier is regenerated
  // on every AGL change so the WHAT-IF folder labels in KMZ stay in sync
  // with the current AGL: e.g. WHATIF-2 (50ft).
  const M_TO_FT = 3.28084;
  const FT_TO_M = 0.3048;
  function formatHypotheticalIdentifier(index: number, aglM: number): string {
    return `WHATIF-${index + 1} (${Math.round(aglM * M_TO_FT)}ft)`;
  }

  const addHypothetical = useCallback((lat: number, lon: number) => {
    setHypotheticals((prev) => {
      const aglM = 30 * FT_TO_M;  // default 30 ft AGL
      const next: ExtraObserver = {
        lat, lon, agl_m: aglM,
        identifier: formatHypotheticalIdentifier(prev.length, aglM),
      };
      return [...prev, next];
    });
    setPlaceMode(false);
  }, []);

  const updateHypotheticalAgl = useCallback((index: number, aglFt: number) => {
    setHypotheticals((prev) =>
      prev.map((h, i) =>
        i === index
          ? { ...h, agl_m: aglFt * FT_TO_M, identifier: formatHypotheticalIdentifier(i, aglFt * FT_TO_M) }
          : h,
      ),
    );
  }, []);

  const deleteHypothetical = useCallback((index: number) => {
    setHypotheticals((prev) => {
      const filtered = prev.filter((_, i) => i !== index);
      // Re-number identifiers so WHATIF-N stays sequential
      return filtered.map((h, i) => ({
        ...h,
        identifier: formatHypotheticalIdentifier(i, h.agl_m),
      }));
    });
  }, []);

  const onStartPlace = useCallback(() => {
    if (!mapRef.current) return;
    placerRef.current?.end();
    setPlaceMode(true);
    placerRef.current = new PlaceMarkerController(
      mapRef.current,
      (lat, lon) => addHypothetical(lat, lon),
      () => setPlaceMode(false),
    );
    placerRef.current.begin();
  }, [addHypothetical]);

  const onClearAllHypotheticals = useCallback(() => {
    setHypotheticals([]);
  }, []);

  // Render hypothetical markers on the map alongside real-station markers.
  // Re-renders any time the hypotheticals list changes (add/delete/AGL edit).
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    for (const m of hypoMarkersRef.current) m.remove();
    hypoMarkersRef.current = [];
    for (let i = 0; i < hypotheticals.length; i++) {
      const h = hypotheticals[i];
      const el = document.createElement("div");
      el.className = "station-marker hypothetical-marker";
      el.dataset.hypothetical = "1";
      el.dataset.connected = "1";
      applyStationMarkerStyle(el, false);
      el.title = h.identifier;
      const marker = new maplibregl.Marker({ element: el }).setLngLat([h.lon, h.lat]).addTo(map);
      hypoMarkersRef.current.push(marker);
    }
    return () => {
      for (const m of hypoMarkersRef.current) m.remove();
      hypoMarkersRef.current = [];
    };
  }, [hypotheticals]);

  // Pick instant-composite resolution from bbox size. The output extent
  // is bbox + ~100 mi station-disk pad on every side, so a small bbox
  // can still produce a region-scale grid. Tiered to keep cell count
  // under the 25M-cell backend cap and the 768 MB API memory ceiling.
  function pickInstantResolution(bbox: BBox): number {
    const R = 3958.7613;  // earth radius in statute miles
    const dlat = ((bbox.lat_max - bbox.lat_min) * Math.PI) / 180;
    const dlon = ((bbox.lon_max - bbox.lon_min) * Math.PI) / 180;
    const lat1 = (bbox.lat_min * Math.PI) / 180;
    const lat2 = (bbox.lat_max * Math.PI) / 180;
    const a = Math.sin(dlat / 2) ** 2 + Math.cos(lat1) * Math.cos(lat2) * Math.sin(dlon / 2) ** 2;
    const diagMi = 2 * R * Math.asin(Math.sqrt(a));
    if (diagMi <= 120) return 200;   // metro: full detail
    if (diagMi <= 250) return 500;   // multi-metro
    return 1000;                     // region-scale (up to 500 mi cap)
  }

  // --- bbox flow ---
  // On bbox completion we try the instant composite first (cache-warm,
  // ~1-3 sec, no Celery job). If no stations are cached for that area
  // we fall back to the existing job-based flow (ModePicker → Celery).
  const tryInstant = useCallback(
    async (bbox: BBox) => {
      const map = mapRef.current;
      if (!map) return;
      // Always try instant on bbox draw — even when hypotheticals are placed.
      // The operator wants to SEE existing coverage first, identify gaps,
      // then place hypotheticals over those gaps. The "Run with N
      // hypotheticals" button in the SidePanel kicks off a job that
      // overlays the hypothetical contributions on top.
      setBboxState("computing");
      try {
        const preview = await api.instantPreview(bbox, frequency, stationFilter);
        if (preview.cached_count === 0) {
          // No cache hits — fall back to the job flow so we can cold-compute
          setBboxState("drawn");
          setShowModePicker(true);
          return;
        }
        const layer = new MinAltitudeRasterLayer();
        await layer.loadCog(api.instantCogUrl(bbox, frequency, pickInstantResolution(bbox), stationFilter));
        unionGridRef.current = layer.getRasterGrid();
        layer.sliderAlt = altitudeFtRef.current;
        layer.opacity = opacityRef.current;
        if (vsLayerRef.current && map.getLayer(vsLayerRef.current.id)) {
          map.removeLayer(vsLayerRef.current.id);
        }
        map.addLayer(
          layer as unknown as maplibregl.CustomLayerInterface,
          map.getLayer("__airspace_fill__") ? "__airspace_fill__" : undefined,
        );
        vsLayerRef.current = layer;
        setInstantBbox(bbox);
        setBboxState("done");
        if (preview.missing_count > 0) {
          setJobMessage(
            `Showing instant coverage for ${preview.cached_count} cached station${preview.cached_count === 1 ? "" : "s"}. ` +
            `${preview.missing_count} station${preview.missing_count === 1 ? "" : "s"} not yet computed — ` +
            `submit a job (Clear bbox + redraw to invoke ModePicker) to cold-compute them.`,
          );
        } else {
          setJobMessage(null);
        }
        drawerRef.current?.clear();
        // Inline stats refresh — refreshStats is declared later in the file
        // so we can't reference it from this callback without a hoist.
        if (layer.totalPixels > 0) {
          setCoverageStats({
            bboxFraction: layer.pctBboxAt(altitudeFt),
            byTier: ALTITUDE_STEPS_FT.map((ft) => ({ ft, pct: layer.pctBboxAt(ft) })),
          });
        }
      } catch (e: any) {
        // Transient API failure must NOT auto-pop ModePicker — only an
        // explicit cached_count=0 from the server should do that.
        setJobMessage(
          `Instant coverage failed: ${e.message ?? e}. ` +
          `Retry by redrawing, or clear the bbox to submit a job.`,
        );
        setBboxState("drawn");
      }
    },
    [frequency, stationFilter, altitudeFt, opacity],
  );

  const startBbox = useCallback(() => {
    if (!mapRef.current) return;
    if (ingestBusy) {
      setJobMessage("Stations are still loading — wait until the banner finishes.");
      return;
    }
    removeCoverageLayer();
    resetObserversState();
    setPendingBbox(null);
    setCurrentJob(null);
    setInstantBbox(null);
    setJobMessage(null);
    const drawer = new BboxDrawer(
      mapRef.current,
      (bbox) => {
        // End the drawer right away on commit — otherwise mouse handlers
        // stay attached + dragPan stays disabled, so any subsequent
        // click+drag tries to draw another bbox instead of panning the
        // map. Operator clicks "Draw bbox" again to redraw.
        drawer.end();
        drawerRef.current = null;
        setPendingBbox(bbox);
        // Don't show ModePicker yet — try instant first. ModePicker pops
        // up only if instant has no cache hits (or fails outright).
        void tryInstant(bbox);
      },
      () => {
        drawer.end();
        drawerRef.current = null;
        setBboxState("idle");
      },
    );
    drawer.begin();
    drawerRef.current = drawer;
    setBboxState("drawing");
  }, [ingestBusy, tryInstant]);

  const clearBbox = useCallback(() => {
    drawerRef.current?.end();
    drawerRef.current = null;
    removeCoverageLayer();
    resetObserversState();
    setPendingBbox(null);
    setCurrentJob(null);
    setInstantBbox(null);
    setJobMessage(null);
    setBboxState("idle");
  }, []);

  const submitJob = useCallback(
    async (mode: JobMode, selectedStationId: number | null, filter: ConnectedFilter, useCache: boolean) => {
      if (!pendingBbox) return;
      setShowModePicker(false);
      setBboxState("computing");
      try {
        const job = await api.createJob({
          bbox: pendingBbox,
          mode,
          selected_station_id: selectedStationId,
          station_filter: filter,
          use_cache: useCache,
          extra_observers: hypotheticals.length > 0 ? hypotheticals : undefined,
        });
        setCurrentJob(job);
        setJobMessage(null);
      } catch (e: any) {
        setJobMessage(`Job creation failed: ${e.message}`);
        setBboxState("drawn");
      }
    },
    [pendingBbox, hypotheticals],
  );

  // Dedicated what-if entry point for the SidePanel's "Run what-if coverage"
  // button. Always uses the current bbox + hypotheticals + connected_only,
  // skipping the ModePicker since the operator's intent is unambiguous when
  // they've placed hypotheticals.
  const submitWhatIf = useCallback(async () => {
    if (!pendingBbox || hypotheticals.length === 0) return;
    await submitJob("union", null, "connected_only", true);
  }, [pendingBbox, hypotheticals, submitJob]);

  // Compute stations inside the pending bbox for the ModePicker station dropdown
  const stationsInBbox = useMemo(() => {
    if (!pendingBbox) return [];
    return stations.filter(
      (s) =>
        s.lon >= pendingBbox.lon_min &&
        s.lon <= pendingBbox.lon_max &&
        s.lat >= pendingBbox.lat_min &&
        s.lat <= pendingBbox.lat_max,
    );
  }, [stations, pendingBbox]);

  // --- viewshed layer management ---
  /** Remove the GL layer + drop cached observer rasters. Does NOT reset the
   * observers list state — callers decide whether to clear that separately.
   * (We want to keep the list when swapping coverage data, only clear on
   * explicit bbox reset.) */
  function removeCoverageLayer() {
    const map = mapRef.current;
    if (map && vsLayerRef.current && map.getLayer(vsLayerRef.current.id)) {
      map.removeLayer(vsLayerRef.current.id);
    }
    vsLayerRef.current = null;
    observerRastersRef.current.clear();
    unionGridRef.current = null;
  }

  function resetObserversState() {
    setObservers([]);
    setEnabledObservers(new Set());
    setCoverageStats(null);
  }

  const onJobDone = useCallback(
    async (ev: Record<string, unknown>) => {
      void ev;
      const job = currentJob;
      if (!job || !mapRef.current) return;
      // Defensive remove: clear ANY layer with this id, not just our
      // tracked vsLayerRef.current. Handles two race patterns:
      //   1. The dropped instant overlay (same id "viewshed-min-alt")
      //   2. A parallel onJobDone that already added its own layer
      //      while this one was awaiting loadCog — without this clear,
      //      the second addLayer would fail with "Layer already exists"
      //      and the failed layer would be assigned to vsLayerRef,
      //      pointing at a phantom not on the map.
      const layer = new MinAltitudeRasterLayer();
      await layer.loadCog(api.cogUrl(job.id, frequency));
      unionGridRef.current = layer.getRasterGrid();
      layer.sliderAlt = altitudeFtRef.current;
      layer.opacity = opacityRef.current;
      if (mapRef.current.getLayer(layer.id)) {
        mapRef.current.removeLayer(layer.id);
      }
      mapRef.current.addLayer(
        layer as unknown as maplibregl.CustomLayerInterface,
        mapRef.current.getLayer("__airspace_fill__") ? "__airspace_fill__" : undefined,
      );
      vsLayerRef.current = layer;
      // The job's union COG is now the source of truth for this overlay,
      // not the instant composite. Clear instantBbox so the frequency-
      // change effect + KMZ export both correctly route through the job
      // path instead of re-fetching the instant overlay.
      setInstantBbox(null);
      setBboxState("done");
      setJobMessage(null);
      drawerRef.current?.clear();
      refreshStats();
      // Show the freshly-completed job's bbox in the saved-jobs overlay
      void savedJobsRef.current?.refresh();

      // Pull observers manifest from the bundle so the contributions UI lights
      // up. Easiest path: fetch the bundle blob, extract just the manifest.json.
      try {
        const r = await fetch(api.bundleUrl(job.id));
        if (r.ok) {
          const buf = await r.arrayBuffer();
          const manifest = await extractManifestJsonFromBundle(buf);
          const obs = (manifest?.observers as ObserverMeta[] | undefined) ?? null;
          if (obs && obs.length > 0) {
            setObservers(obs);
            setEnabledObservers(new Set(obs.map((o) => o.station_id)));
          }
        }
      } catch {
        /* manifest fetch is best-effort; UI gracefully hides if absent */
      }
    },
    [currentJob, frequency, altitudeFt, opacity],
  );

  const onJobFailed = useCallback((ev: Record<string, unknown>) => {
    setBboxState("drawn");
    setJobMessage(`Job failed: ${ev.error}`);
  }, []);

  // Poll national-overlay status (cached input count + built/stale) so the
  // toggle in the side panel can show "X stations cached" without forcing
  // a 30+ s rebuild every time the user opens the panel.
  useEffect(() => {
    let cancelled = false;
    const poll = async () => {
      try {
        const s = await api.nationalStatus(frequency, nationalRegion);
        if (!cancelled) setNationalStatus({
          inputs: s.inputs, built: s.built, stale: s.stale, size_bytes: s.size_bytes,
        });
      } catch {
        if (!cancelled) setNationalStatus(null);
      }
    };
    poll();
    const id = window.setInterval(poll, 15000);
    return () => { cancelled = true; window.clearInterval(id); };
  }, [frequency, nationalRegion]);

  // National coverage envelope: load on enable, remove on disable. Re-loads
  // when the frequency changes while still enabled. Sets nationalLoading
  // true during the (potentially slow, ~30-60 s cold) fetch so the toggle
  // can show a spinner.
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    if (!nationalEnabled) {
      // Nothing to do here — toggling off is handled in onToggleNational
      // because we want to also reset bbox state.
      return;
    }
    let cancelled = false;
    setNationalLoading(true);
    (async () => {
      try {
        const layer = new MinAltitudeRasterLayer("viewshed-national");
        await layer.loadCog(api.nationalCogUrl(frequency, nationalRegion));
        if (cancelled) return;
        // Re-attach: removeCoverageLayer cleared whatever was there before
        if (vsLayerRef.current && map.getLayer(vsLayerRef.current.id)) {
          map.removeLayer(vsLayerRef.current.id);
        }
        unionGridRef.current = layer.getRasterGrid();
        layer.sliderAlt = altitudeFtRef.current;
        layer.opacity = opacityRef.current;
        map.addLayer(
          layer as unknown as maplibregl.CustomLayerInterface,
          map.getLayer("__airspace_fill__") ? "__airspace_fill__" : undefined,
        );
        vsLayerRef.current = layer;
        refreshStats();
      } catch (e: any) {
        if (!cancelled) setJobMessage(`National overlay failed: ${e.message ?? e}`);
      } finally {
        if (!cancelled) setNationalLoading(false);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [nationalEnabled, frequency, nationalRegion]);

  const onToggleNational = useCallback((on: boolean) => {
    if (on) {
      // Tear down per-bbox state so the two views don't coexist confusingly
      drawerRef.current?.end();
      drawerRef.current = null;
      removeCoverageLayer();
      resetObserversState();
      setPendingBbox(null);
      setCurrentJob(null);
      setJobMessage(null);
      setBboxState("idle");
      setNationalEnabled(true);
    } else {
      removeCoverageLayer();
      setNationalEnabled(false);
    }
  }, []);

  // Region dropdown: switch which region's overlay we render. The overlay-load
  // effect re-fires on nationalRegion change to fetch that region's COG; here
  // we also fly the map to the region so the (otherwise off-screen) overlay is
  // actually in view.
  const onChangeNationalRegion = useCallback((region: NationalRegion) => {
    setNationalRegion(region);
    mapRef.current?.fitBounds(NATIONAL_REGION_BOUNDS[region], { padding: 40, duration: 600 });
  }, []);

  // Reload COG when frequency CHANGES — not when currentJob does. Loading a
  // saved/own job is handled by onLoadSavedJob / onJobDone explicitly; if we
  // also fire here on currentJob change, the async loadCog races the
  // composite useEffect and sometimes lands second, overwriting the
  // per-station composite with the full union and wedging the contribution
  // checkboxes (every toggle gets clobbered on the next render).
  useEffect(() => {
    const layer = vsLayerRef.current;
    if (!layer) return;
    if (instantBbox) {
      // Instant overlay: re-fetch the composite at the new frequency
      layer.loadCog(api.instantCogUrl(instantBbox, frequency, pickInstantResolution(instantBbox), stationFilter)).then(() => {
        layer.markDirty();
        mapRef.current?.triggerRepaint();
      });
      return;
    }
    const job = currentJob;
    if (job && job.status === "done") {
      layer.loadCog(api.cogUrl(job.id, frequency)).then(() => {
        layer.markDirty();
        mapRef.current?.triggerRepaint();
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [frequency]);

  // Update shader uniforms on slider / opacity change. Within a useEffect
  // the state value is the LATEST value (effects fire after commit), so
  // we use altitudeFt + opacity directly here — the refs are only needed
  // for INLINE assignments inside other callbacks where the closure could
  // be stale.
  useEffect(() => {
    const layer = vsLayerRef.current;
    const m = mapRef.current;
    if (!layer || !m) return;
    layer.sliderAlt = altitudeFt;
    layer.opacity = opacity;
    // MapLibre 4.x sometimes skips a custom-layer render when only uniforms
    // change because it sees no style mutation to invalidate. Force a real
    // style mutation MapLibre detects: re-set the bearing to its current
    // value (no visible change but the style update path runs and triggers
    // a render pass that calls our custom layer's render(), reading the
    // updated uniforms). triggerRepaint + rAF as belt-and-suspenders.
    layer.markDirty();
    try {
      m.setBearing(m.getBearing());
    } catch { /* basemap swap mid-flight; next render picks it up */ }
    m.triggerRepaint();
    requestAnimationFrame(() => m.triggerRepaint());
    // Update current-altitude stat
    if (layer.totalPixels > 0) {
      setCoverageStats({
        bboxFraction: layer.pctBboxAt(altitudeFt),
        byTier: ALTITUDE_STEPS_FT.map((ft) => ({ ft, pct: layer.pctBboxAt(ft) })),
      });
    }
  }, [altitudeFt, opacity]);

  // Recompute stats when the raster itself changes (fresh job done, bundle loaded,
  // frequency switch, composite toggle). The layer recomputes internal sorted
  // array in _computeStats; we just need to re-read.
  const refreshStats = useCallback(() => {
    const layer = vsLayerRef.current;
    if (!layer || layer.totalPixels === 0) {
      setCoverageStats(null);
      return;
    }
    setCoverageStats({
      bboxFraction: layer.pctBboxAt(altitudeFt),
      byTier: ALTITUDE_STEPS_FT.map((ft) => ({ ft, pct: layer.pctBboxAt(ft) })),
    });
  }, [altitudeFt]);

  const onAltitudeChange = useCallback((ft: number) => setAltitudeFt(snapAltitude(ft)), []);

  const [exportBusy, setExportBusy] = useState(false);

  const onExportKmz = useCallback(async () => {
    if (!currentJob && !instantBbox) return;
    if (currentJob && observers.length > 0 && enabledObservers.size === 0) {
      setJobMessage("Check at least one station in Coverage contributions to export.");
      return;
    }
    setExportBusy(true);
    try {
      let blob: Blob;
      let downloadName: string;
      if (instantBbox && !currentJob) {
        // Instant mode: build the KMZ directly from cache, no job needed
        blob = await api.instantExport(instantBbox, "kmz", undefined, stationFilter);
        downloadName = `coverage_instant.kmz`;
      } else if (currentJob) {
        const body = observers.length > 0
          ? { station_ids: Array.from(enabledObservers) }
          : {};
        const res = await fetch(api.exportKmzUrl(currentJob.id), {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(body),
        });
        if (!res.ok) {
          const detail = await res.text();
          setJobMessage(`Export failed: ${res.status} ${detail}`);
          return;
        }
        blob = await res.blob();
        downloadName = `coverage_${currentJob.id.slice(0, 8)}.kmz`;
      } else {
        return;
      }
      const href = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = href;
      a.download = downloadName;
      // Some browsers ignore .click() on a detached anchor — append to body
      // first, then click, then clean up. Was causing the "have to hit
      // export twice before save dialog" bug.
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      // Defer revoke so the browser has a chance to start the download
      setTimeout(() => URL.revokeObjectURL(href), 1000);
      setJobMessage(null);
    } catch (e: any) {
      setJobMessage(`Export failed: ${e.message}`);
    } finally {
      setExportBusy(false);
    }
  }, [currentJob, observers, enabledObservers]);

  return (
    <div style={{ position: "relative", width: "100vw", height: "100vh" }}>
      <div ref={mapContainer} style={{ position: "absolute", inset: 0 }} />
      <div style={{
        position: "absolute", right: 6, bottom: 6, zIndex: 12,
        fontFamily: "monospace", fontSize: 10, color: "#666",
        background: "rgba(0,0,0,0.4)", padding: "2px 6px", borderRadius: 3,
        pointerEvents: "none",
      }}>
        build 2026-05-04k (slider works for national overlay)
      </div>
      <IngestBanner onFirstSuccess={refreshStations} />
      <SidePanel
        basemap={basemap}
        onBasemapChange={setBasemap}
        altitudeFt={altitudeFt}
        onAltitudeChange={onAltitudeChange}
        frequency={frequency}
        onFrequencyChange={setFrequency}
        opacity={opacity}
        onOpacityChange={setOpacity}
        stationFilter={stationFilter}
        onStationFilterChange={setStationFilter}
        stationCount={stationCount}
        hasCoverage={bboxState === "done" || nationalEnabled}
        onExportKmz={onExportKmz}
        exportBusy={exportBusy}
        bboxState={bboxState}
        onStartBbox={startBbox}
        onClearBbox={clearBbox}
        selectedStation={selectedStation}
        jobMessage={jobMessage}
        onUploadCsv={onUploadCsv}
        onReingestSeed={onReingestSeed}
        uploadDisabled={ingestBusy}
        airspaceEnabled={airspaceEnabled}
        onAirspaceToggle={onAirspaceToggle}
        airspaceOpacity={airspaceOpacity}
        onAirspaceOpacityChange={(id, o) =>
          setAirspaceOpacity((prev) => ({ ...prev, [id]: o }))
        }
        onSaveBundle={onSaveBundle}
        onLoadBundle={onLoadBundle}
        showSavedJobs={showSavedJobs}
        onToggleSavedJobs={setShowSavedJobs}
        nationalEnabled={nationalEnabled}
        onToggleNational={onToggleNational}
        nationalLoading={nationalLoading}
        nationalStatus={nationalStatus}
        nationalRegion={nationalRegion}
        onChangeNationalRegion={onChangeNationalRegion}
        contributions={observers.map((o) => ({
          station_id: o.station_id,
          identifier: o.identifier,
          merged_member_ids: o.merged_member_ids,
          cache_hit: o.cache_hit,
        }))}
        enabledContributions={enabledObservers}
        onToggleContribution={onToggleContribution}
        onContributionsAllOn={onContributionsAllOn}
        onContributionsAllOff={onContributionsAllOff}
        coverageStats={coverageStats}
        hypotheticals={hypotheticals}
        placeMode={placeMode}
        onStartPlace={onStartPlace}
        onUpdateHypotheticalAgl={updateHypotheticalAgl}
        onDeleteHypothetical={deleteHypothetical}
        onClearAllHypotheticals={onClearAllHypotheticals}
        onSubmitWhatIf={submitWhatIf}
        canRunWhatIf={
          hypotheticals.length > 0 &&
          (pendingBbox != null || instantBbox != null) &&
          bboxState !== "computing"
        }
        overlaySource={
          currentJob && (currentJob.extra_observers?.length ?? 0) > 0
            ? { kind: "with-hypotheticals", count: currentJob.extra_observers!.length }
            : currentJob
            ? { kind: "job" }
            : instantBbox
            ? { kind: "existing" }
            : null
        }
      />
      {showModePicker && pendingBbox && (
        <ModePicker
          stationsInBbox={stationsInBbox}
          stationFilter={stationFilter}
          onCancel={() => {
            setShowModePicker(false);
            clearBbox();
          }}
          onSubmit={submitJob}
        />
      )}
      {(bboxState === "computing") || externalJobs.length > 0 || airspaceStatus !== "idle" ? (
        <div style={{ position: "absolute", left: 16, bottom: 16, width: 340, zIndex: 11,
                      display: "flex", flexDirection: "column", gap: 8 }}>
          {currentJob && bboxState === "computing" && (
            <JobProgress
              jobId={currentJob.id}
              onDone={onJobDone}
              onFailed={onJobFailed}
              onCancel={onCancelOwnJob}
            />
          )}
          {!currentJob && bboxState === "computing" && (
            <div style={{
              background: "rgba(20,20,28,0.92)", color: "#fff",
              padding: "10px 14px", borderRadius: 6,
              fontFamily: "monospace", fontSize: 12,
              border: "1px solid #3a3a4a", display: "flex", alignItems: "center", gap: 10,
            }}>
              <div style={{
                width: 12, height: 12, borderRadius: "50%",
                border: "2px solid #fa3", borderTopColor: "transparent",
                animation: "spin 0.8s linear infinite",
              }} />
              <span>Compositing instant coverage…</span>
            </div>
          )}
          {airspaceStatus !== "idle" && (
            <div style={{
              background: "rgba(20,20,28,0.92)",
              color: airspaceStatus === "error" ? "#f55" : "#fff",
              padding: "8px 12px", borderRadius: 6,
              fontFamily: "monospace", fontSize: 11,
              border: `1px solid ${airspaceStatus === "error" ? "#a33" : "#3a3a4a"}`,
              display: "flex", alignItems: "center", gap: 10,
            }}>
              {airspaceStatus === "loading" ? (
                <>
                  <div style={{
                    width: 10, height: 10, borderRadius: "50%",
                    border: "2px solid #6cf", borderTopColor: "transparent",
                    animation: "spin 0.8s linear infinite",
                  }} />
                  <span>Loading airspace{airspaceError ? ` (${airspaceError})` : "…"}</span>
                </>
              ) : (
                <>
                  <span style={{ color: "#f55" }}>⚠</span>
                  <span style={{ flex: 1 }}>Airspace fetch failed: {airspaceError ?? "unknown"}</span>
                  <button
                    style={{
                      background: "#3a3a4a", color: "#fff", border: "none",
                      padding: "3px 8px", borderRadius: 3, cursor: "pointer", fontSize: 11,
                    }}
                    onClick={() => airspaceRef.current?.setEnabled(Array.from(airspaceEnabled))}
                  >
                    Retry
                  </button>
                </>
              )}
            </div>
          )}
          {externalJobs.map((j) => (
            <div key={j.id}>
              <div style={{ fontSize: 11, color: "#fa3", marginBottom: 2, paddingLeft: 4 }}>
                ↻ Server-side job (not submitted from this tab)
              </div>
              <JobProgress
                jobId={j.id}
                onDone={() => onExternalJobDone(j.id)}
                onFailed={(ev) => onExternalJobFailed(j.id, ev)}
                onCancel={() => onCancelExternalJob(j.id)}
              />
            </div>
          ))}
        </div>
      ) : null}
      {selectedStation && (
        <StationDiagPopup
          stationId={selectedStation.id}
          onClose={() => setSelectedStation(null)}
        />
      )}
    </div>
  );
}
